import { z } from "zod";
/**
 * The notification event catalogue, its audiences, its deep links — and the
 * e-way bill rule that raises the flagship event (spec §17.7, Rule 138 CGST).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THE E-WAY BILL THRESHOLDS LIVE IN THE SAME FILE AS THE EVENTS.
 *
 * They are the same subject read from two ends. The threshold is not a display
 * rule and not a tax figure — nothing on a printed document derives from it. It
 * is the SINGLE CONDITION under which the app must tell a human that goods may
 * not leave the yard yet. Splitting "the number" from "who gets told" would put
 * a legal parameter in one file and the only thing that reads it in another, and
 * the next person to raise the Maharashtra limit would find one of them.
 *
 * NOTHING HERE IS TAX ARITHMETIC. Every rupee that reaches a document is
 * computed in `@steelbill/tax` (CLAUDE.md); this module only COMPARES totals the
 * engine already produced against a statutory limit. No division, no rounding,
 * no rate.
 * ═════════════════════════════════════════════════════════════════════════════
 */
/* -------------------------------------------------------------------------- */
/* 1. The e-way bill thresholds — Rule 138(1) CGST                             */
/* -------------------------------------------------------------------------- */
/**
 * ═════════════════════════════════════════════════════════════════════════════
 * THE CONSIGNMENT VALUE ABOVE WHICH AN E-WAY BILL IS REQUIRED, IN INTEGER PAISE.
 * ONE PLACE. BOTH FIGURES. THEY ARE NOT THE SAME NUMBER, AND THAT IS THE POINT.
 *
 *   INTER_STATE  ₹50,000    — Rule 138(1) CGST Rules, 2017. UNIFORM ACROSS
 *                             INDIA. Parliament sets it; no state may vary it.
 *
 *   INTRA_STATE  ₹1,00,000  — MAHARASHTRA ONLY. Rule 138(1)'s proviso lets each
 *                             STATE set its own limit for movement that begins
 *                             and ends inside it, and Maharashtra RAISED ITS
 *                             LIMIT FROM ₹50,000 TO ₹1,00,000 by Notification
 *                             No. 15E/2018 (State Tax), 29.06.2018. Other states
 *                             did not: several still sit at ₹50,000, and a few
 *                             exempt intra-city movement entirely.
 *
 * SO THIS IS A LEGAL PARAMETER, NOT A MAGIC NUMBER. If this business ever
 * dispatches from a second state, the intra-state figure becomes a function of
 * the DISPATCH state rather than a constant, and this is the line that has to
 * change. Today every dispatch leaves Pashan, Pune, so Maharashtra's figure is
 * the only intra-state one that can apply.
 *
 * A SINGLE SHARED CONSTANT WOULD BE WRONG IN EXACTLY ONE DIRECTION AND NOBODY
 * WOULD NOTICE WHICH. Use ₹1,00,000 for both and every ₹60,000 consignment to
 * Gujarat moves with no e-way bill — a detention, 200% of the tax as penalty,
 * and the lorry held. Use ₹50,000 for both and the app nags on every ₹60,000
 * Pune job for ever, which trains the owner to ignore it, which is the same
 * failure arriving later.
 *
 * NOT A DATABASE SETTING AND NOT VERSIONED LIKE `hsn_codes` (§8.7). A GST RATE
 * prints on a legal document and must be reproducible for a document issued
 * years ago; this figure prints nowhere and decides only whether somebody is
 * told something today. Maharashtra's limit has not moved since 2018, and if it
 * does, the fix is this line plus a deploy.
 *
 * SOURCES, so the next reader can check rather than trust:
 *   - Rule 138(1), CGST Rules 2017 — the ₹50,000 base and the "exceeding" wording
 *   - Maharashtra Notification No. 15E/2018 — State Tax, dated 29.06.2018
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const EWAY_BILL_THRESHOLD_PAISE = Object.freeze({
    /** ₹50,000 — Rule 138(1), uniform across India. */
    INTER_STATE: 5_000_000,
    /** ₹1,00,000 — Maharashtra Notification 15E/2018. A STATE figure; see above. */
    INTRA_STATE: 10_000_000,
});
/** The threshold that applies to a movement of this supply type, in paise. */
export function ewayBillThresholdPaise(supplyType) {
    /*
     * DENY BY DEFAULT, in the same spirit as `can()`: anything that is not
     * literally `INTRA_STATE` gets the LOWER, national figure. An unrecognised
     * supply type then over-warns rather than letting a consignment move
     * unflagged, and the two named values are compared rather than indexed, so an
     * untrusted string can never walk `Object.prototype` into a `number`.
     */
    return supplyType === "INTRA_STATE"
        ? EWAY_BILL_THRESHOLD_PAISE.INTRA_STATE
        : EWAY_BILL_THRESHOLD_PAISE.INTER_STATE;
}
/**
 * Does this document's movement need an e-way bill? (Rule 138(1))
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE DECISIONS ARE BAKED IN HERE, EACH WITH ITS REASON.
 *
 * 1. THE COMPARISON IS STRICTLY GREATER-THAN. Rule 138(1) says "consignment
 *    value of goods EXCEEDS fifty thousand rupees". A bill of exactly ₹50,000
 *    inter-state, or exactly ₹1,00,000 intra-state, does NOT require one. That
 *    is statute text, not a judgement call, and it is asserted at the boundary
 *    in the tests rather than left to a reader's memory of `>` versus `>=`.
 *
 * 2. THE FIGURE IS THE VALUE INCLUDING TAX. "Consignment value" is defined in
 *    the explanation to Rule 138(1) as the value declared in the invoice
 *    INCLUDING the central, state and integrated tax charged on it. Comparing
 *    the taxable subtotal instead would let an ₹96,000 + 18% bill (₹1,13,280)
 *    move unflagged.
 *
 * 3. A PURE-SERVICE BILL NEVER QUALIFIES, AT ANY VALUE. The obligation attaches
 *    to the MOVEMENT OF GOODS, not to the size of an invoice. A ₹5,00,000
 *    labour-only works-contract bill with nothing leaving the workshop needs no
 *    e-way bill. On a MIXED bill only the goods lines count towards the
 *    consignment value (CBIC FAQ), which is why the whole-document total is used
 *    only when every billable line is goods.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function ewayBillRequirement(input) {
    const thresholdPaise = ewayBillThresholdPaise(input.supplyType);
    // A group header contributes no value of its own (§4), so a zero-valued line
    // is skipped whichever kind it claims to be — it cannot make a bill "goods".
    const billable = input.lines.filter((line) => line.taxablePaise > 0);
    const goods = billable.filter((line) => !line.isService);
    if (goods.length === 0) {
        return { required: false, consignmentPaise: 0, thresholdPaise };
    }
    /*
     * ALL GOODS → the document's own grand total, which is the one figure that
     * carries the round-off (§5) and therefore matches what the portal is told.
     * MIXED → the goods lines only, each with its own tax, because the service
     * portion of the bill is not travelling anywhere.
     */
    const consignmentPaise = goods.length === billable.length
        ? input.grandTotalPaise
        : goods.reduce((sum, line) => sum + line.taxablePaise + line.taxPaise, 0);
    return {
        required: consignmentPaise > thresholdPaise,
        consignmentPaise,
        thresholdPaise,
    };
}
/* -------------------------------------------------------------------------- */
/* 2. The e-way bill NUMBER — recorded, never generated                        */
/* -------------------------------------------------------------------------- */
/**
 * A 12-digit e-way bill number, exactly as the NIC portal issues it.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * SHAPE ONLY. THIS APP NEVER INVENTS ONE, AND NEVER WILL.
 *
 * Direct e-way bill API access requires turnover above ₹10 crore; below that it
 * needs a paid GSP. This business is far below either, so the flow is and stays:
 * the CA generates on the government portal, the portal issues the number and
 * its own PDF carrying NIC's QR, and THAT sheet travels with the driver. What
 * gets typed here is a record of a fact the government already created.
 *
 * THE INVOICE PDF DOES DRAW A QR FROM IT (owner's decision, 2026-08-17; §10.1
 * amendment). The e-way bill QR is plain data — EBN, supplier GSTIN, generation
 * date — with no signature over it, exactly as TallyPrime builds it locally, so
 * reproducing it claims nothing about who raised the bill. Recording a number
 * here is still all this app does; the drawing is `ewayBillQrPayload` in
 * apps/api/src/services/pdf/theme.ts, and both invoice templates carry it
 * (§18.3). The SPA draws none — the paper is where a checking officer scans.
 * (The E-INVOICE IRN QR is the signed one that genuinely cannot be reproduced —
 * different artifact, different columns, see `invoice.record_irn`.)
 *
 * THE LENGTH IS THE CHECK THAT EARNS ITS KEEP. A truncated or over-long paste is
 * the failure a copy actually produces, and an 11-digit number stored looking
 * valid is worse than a refusal.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const EWAY_BILL_NUMBER_PATTERN = /^\d{12}$/;
export const EwayBillNumberSchema = z
    .string()
    .trim()
    .regex(EWAY_BILL_NUMBER_PATTERN, "An e-way bill number is exactly 12 digits, as issued by the portal — copy it from the e-way bill sheet.");
/* -------------------------------------------------------------------------- */
/* 3. The event catalogue                                                      */
/* -------------------------------------------------------------------------- */
/**
 * Every notification event, as namespaced `resource.thing.happened` strings.
 *
 * A `const` tuple with the union derived from it, exactly like `CAPABILITIES` —
 * so a typo in an emit call is a COMPILE ERROR rather than a row nobody ever
 * renders.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NOT EVERY EVENT BELOW HAS AN EMITTER YET, AND THE LIST SAYS WHICH.
 * The three `eway_bill` events are wired into the invoice and challan write
 * paths; `challan.unbilled` and `quotation.expiring` are wired into the
 * SCHEDULED sweep (see {@link SCHEDULED_NOTIFICATION_DAYS}). The last two are
 * declared because the hub renders whatever it is given and the catalogue is the
 * contract the client's strings are keyed on — each names below exactly what it
 * is waiting for. A declared event with no emitter renders nothing; an
 * UNDECLARED one emitted by a later change would render as an unknown key.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const NOTIFICATION_EVENTS = [
    /**
     * WIRED. An invoice was issued whose consignment value crosses the Rule 138
     * threshold, so an e-way bill must exist before the goods move. THE FLAGSHIP
     * EVENT: nothing told anyone before this, and a ₹1.2 lakh job going out
     * unnoticed is a penalty, not an untidiness.
     */
    "invoice.eway_bill.required",
    /** WIRED. The 12-digit number came back from the portal and was recorded. */
    "invoice.eway_bill.recorded",
    /** WIRED. The same, for goods moving under Rule 55 with no invoice behind them. */
    "challan.eway_bill.recorded",
    /**
     * WIRED, TO THE SWEEP RATHER THAN TO A REQUEST (2026-08-17). A delivery has
     * been out {@link SCHEDULED_NOTIFICATION_DAYS.unbilledChallan} days with no
     * bill. `unbilledChallanWhere()` answers "which ones" as a derived read on the
     * home screen; the alert is the same predicate with an age bound, raised by
     * `services/scheduled-notifications.ts` from a cron entry (md/deploy.md).
     */
    "challan.unbilled",
    /**
     * WIRED, TO THE SAME SWEEP. `quotations.valid_until` is stored and now read:
     * a quotation lapsing within
     * {@link SCHEDULED_NOTIFICATION_DAYS.quotationExpiryNotice} days.
     */
    "quotation.expiring",
    /** NOT WIRED — one call in `routes/payments.ts`, inside its existing transaction. */
    "payment.recorded",
    /** NOT WIRED — one call in `routes/credit-notes.ts`, inside its transaction. */
    "creditnote.issued",
];
export const NotificationEventSchema = z.enum(NOTIFICATION_EVENTS);
export function isNotificationEvent(value) {
    return typeof value === "string" && NOTIFICATION_EVENTS.includes(value);
}
/* -------------------------------------------------------------------------- */
/* 3A. The two TIME-BASED events — how many days                               */
/* -------------------------------------------------------------------------- */
/**
 * ═════════════════════════════════════════════════════════════════════════════
 * TWO BUSINESS JUDGEMENTS, NAMED AND ARGUED RATHER THAN INLINED.
 *
 * Every other event in this catalogue fires because somebody made a request.
 * These two fire because a DAY PASSED, which nobody makes a request for — so
 * `services/scheduled-notifications.ts` sweeps for them and a cron entry calls
 * it (the line is in md/deploy.md). Both numbers below are judgements about how
 * this business runs, not facts about GST, so they are here where the events are
 * declared and they are the ONE place either is written down. Changing a number
 * here is the whole change; nothing else reads a literal.
 *
 * CONTRAST WITH `EWAY_BILL_THRESHOLD_PAISE` ABOVE, which shares this file for a
 * different reason: that one is Rule 138 and a state notification, and getting
 * it wrong is a detention. These are tuning. Too short and the owner learns to
 * ignore his inbox, which costs exactly the signal the feature was built to give
 * him; too long and the reminder arrives after the money is already old.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const SCHEDULED_NOTIFICATION_DAYS = Object.freeze({
    /**
     * SEVEN DAYS — "this delivery went out a week ago and still has no bill".
     *
     * WHY SEVEN. §17.7's whole premise is that "unbilled deliveries are exactly
     * how a small fabricator quietly loses money", and the two challan types this
     * counts (SUPPLY_NOT_INVOICED and APPROVAL — see `BILLABLE_CHALLAN_TYPES`) are
     * the ones where a bill is genuinely expected to follow. A day or two of lag
     * is ordinary: the material goes to site on Tuesday and the invoice is written
     * when the measurement sheet comes back. A week is not ordinary — it is the
     * point at which "I will bill it tomorrow" has stopped being true.
     *
     * SEVEN ALSO KEEPS THE BILL INSIDE ITS OWN TAX PERIOD for three weeks in four.
     * GSTR-1 is filed monthly (§17.4) and s.31(1) CGST already wanted the invoice
     * at or before removal of the goods, so a delivery drifting past the month end
     * unbilled moves the output tax into the wrong return and makes the reconcile
     * the CA does a correction rather than a tick. A 14-day threshold would let
     * mid-month deliveries slip the month routinely; 7 does not.
     *
     * AND IT MATCHES HOW THE OWNER WORKS. He is on the workshop floor, not in the
     * app hourly; a weekly rhythm is one he already has. A 3-day alert on a
     * business that bills in batches would fire on deliveries he is actively
     * working through, and an alert that is usually wrong is one he stops reading.
     *
     * THE BOUNDARY: a challan dated EXACTLY seven days ago DOES raise the alert —
     * the comparison is `challan_date <= today − 7 days`, inclusive. "Out for a
     * week" reads as a week having elapsed, so the seventh morning is when it is
     * true, not the eighth.
     */
    unbilledChallan: 7,
    /**
     * SEVEN DAYS — "this quotation lapses within a week".
     *
     * WHY SEVEN. The point of the alert is to leave enough time to DO something,
     * and the something is not clerical: re-quoting means checking what steel
     * costs this week, re-pricing the job and ringing the customer, and that is a
     * couple of working days plus the customer's own delay in answering. A day or
     * two of notice would tell him about a price that has already effectively
     * gone. Much more than a week and the reminder arrives while the quotation is
     * still perfectly live, so it is filed as noise and the real one behind it is
     * filed with it.
     *
     * SEVEN MATCHES THE OTHER FIGURE ON PURPOSE. Both alerts land in the same
     * inbox, and one weekly rhythm is easier to trust than two competing ones.
     * They are separate constants all the same, because they answer to different
     * things: this one to how long a re-quote takes, the other to the tax period.
     *
     * THE BOUNDARY: a quotation whose `valid_until` is EXACTLY seven days away
     * DOES raise the alert — the window is `today <= valid_until <= today + 7`,
     * inclusive at both ends, so it fires on the first morning the quotation
     * enters the window and (by the unique index) not again while it stays there.
     * A quotation that has ALREADY lapsed raises nothing: the event is
     * `quotation.expiring`, not `expired`, and back-filling every historically
     * lapsed quotation on the first run would bury the ones still worth saving.
     */
    quotationExpiryNotice: 7,
});
/* -------------------------------------------------------------------------- */
/* 4. Who gets told — by CAPABILITY, never by role (§2.1)                       */
/* -------------------------------------------------------------------------- */
/**
 * The capability that defines each event's audience.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * "NOTIFY THE CA" AND "NOTIFY THE OWNER" ARE REQUIREMENTS WRITTEN IN PEOPLE.
 * THEY ARE IMPLEMENTED IN CAPABILITIES, AND THE TRANSLATION IS DELIBERATE.
 *
 * §2.1's rule is absolute: never write `if (user.role === "ADMIN")`. A recipient
 * list keyed on roles is that check wearing a different hat — the day an
 * ACCOUNTANT or a SUPERVISOR is added, every event would need revisiting, which
 * is exactly the hunt §2.1 exists to prevent.
 *
 * THE RULE FOR CHOOSING ONE: pick the capability that names THE ACTION THE
 * NOTIFICATION IS ASKING FOR. A notification is a request to do something, so
 * the people who can do it are the people to tell. That makes each choice
 * checkable rather than a matter of taste:
 *
 *   - a bill crossed the threshold  → somebody must GENERATE an e-way bill and
 *     type the number back, so it goes to everyone holding `eway.record` — the
 *     CA above all, and the owner and the son who are also at a keyboard.
 *   - a number was recorded         → the people who RAISE AND DISPATCH bills
 *     need to know the goods are cleared to move: `invoice.create`. The CA does
 *     not hold it, which is how "not the CA who did it" is structural rather
 *     than a role name in a handler.
 *   - a delivery is unbilled        → somebody must RAISE THE BILL:
 *     `invoice.create`.
 *   - a quotation is expiring       → somebody must re-quote: `quotation.create`.
 *   - a payment was recorded        → the people who keep the ledger:
 *     `payment.record`.
 *   - a credit note was issued      → it reduces declared output tax, so it goes
 *     to the administration surface that answers for the books: `audit.view`.
 *
 * THE ACTOR IS ALWAYS EXCLUDED, in `services/notifications.ts` rather than here:
 * telling someone what they have just done is how an inbox becomes noise.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const NOTIFICATION_EVENT_AUDIENCE = Object.freeze({
    "invoice.eway_bill.required": "eway.record",
    "invoice.eway_bill.recorded": "invoice.create",
    "challan.eway_bill.recorded": "invoice.create",
    "challan.unbilled": "invoice.create",
    "quotation.expiring": "quotation.create",
    "payment.recorded": "payment.record",
    "creditnote.issued": "audit.view",
});
/**
 * Whether the person who caused the event is one of the people told about it.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * "THE ACTOR NEVER HEARS ABOUT THEIR OWN ACTION" IS A GOOD RULE THAT IS WRONG
 * FOR EXACTLY ONE EVENT IN THIS CATALOGUE, AND IT IS THE FLAGSHIP ONE.
 *
 * The rule is right whenever a notification REPORTS AN ACTION: "the number was
 * recorded" is not news to the CA who typed it, and an inbox that echoes what you
 * just did is one you stop reading — with the message that mattered somewhere in
 * it.
 *
 * `invoice.eway_bill.required` IS NOT A REPORT OF AN ACTION. It is a DERIVED
 * LEGAL CONSEQUENCE of one: Rule 138's threshold, against this bill's supply
 * type, against the goods-only portion of its consignment value including tax.
 * The person typing the bill has not computed any of that and cannot see it while
 * typing — and he is the very person about to load the lorry. Excluding him made
 * the one alert that exists to catch a dispatch reach everybody EXCEPT the
 * dispatcher.
 *
 * IT MATTERS MORE HERE THAN THE RULE IT BREAKS, because of how small the
 * audience is. `eway.record` is held by the OWNER, the ADMIN and the ACCOUNTANT
 * — three people, and the CA only while `accountant_role` (§18.5) is switched
 * on, which it ships OFF. So with the actor excluded an owner-written bill can
 * reach exactly one reachable human. If the son is not in the app that day,
 * nobody is told at all — which is the ₹1.2 lakh job going out unnoticed that
 * this whole feature exists to prevent. Her login shipping does not repair that:
 * an audience that depends on a flag is not an audience to design around.
 *
 * THE CAPABILITY STILL DECIDES. This map only stops the actor being SUBTRACTED
 * from an audience; it never adds anyone to one. Someone without `eway.record`
 * who writes a bill over the threshold is still told nothing, because he was
 * never in the audience to begin with (§2.1).
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const NOTIFICATION_TELLS_THE_ACTOR = Object.freeze({
    /** A derived legal consequence, not a report — see above. */
    "invoice.eway_bill.required": true,
    /** He typed the twelve digits himself. */
    "invoice.eway_bill.recorded": false,
    "challan.eway_bill.recorded": false,
    /**
     * System-raised by the sweep, which passes `actorUserId: null`, so there is
     * no actor to subtract and this value never gets consulted. It stays `false`
     * rather than `true` because the honest reading of the rule is unchanged: if
     * a person ever did trigger one of these, telling them what they just did
     * would still be noise.
     */
    "challan.unbilled": false,
    "quotation.expiring": false,
    "payment.recorded": false,
    "creditnote.issued": false,
});
/* -------------------------------------------------------------------------- */
/* 5. The deep link — a notification that cannot be acted on is a nag           */
/* -------------------------------------------------------------------------- */
/**
 * The document kinds a notification can point at.
 *
 * A PAYMENT IS NOT ONE OF THEM, on purpose. The SPA has no payment detail screen
 * — a receipt is read on the bill it was paid against — so a payment
 * notification carries `entityType: "invoice"` and the payment's own details in
 * its payload. The rule is that `entityType` names WHAT THE LINK OPENS, not what
 * happened, because a link to a screen that does not exist is the one failure a
 * deep link cannot survive.
 */
export const NOTIFICATION_ENTITY_TYPES = [
    "invoice",
    "challan",
    "quotation",
    "creditNote",
];
export const NotificationEntityTypeSchema = z.enum(NOTIFICATION_ENTITY_TYPES);
/**
 * SPA paths, kept in step with `apps/app/src/routes/paths.ts` by hand.
 *
 * The server builds the link rather than the client, so the notification row and
 * the thing it opens are decided in one place — and so a future channel that is
 * NOT the SPA (an email digest, §17A) sends the same destination rather than
 * inventing a second URL scheme.
 */
const NOTIFICATION_LINK_PREFIX = Object.freeze({
    invoice: "/invoices",
    challan: "/challans",
    quotation: "/quotations",
    creditNote: "/credit-notes",
});
/**
 * Where tapping this notification lands.
 *
 * EVERY NOTIFICATION DEEP-LINKS TO THE DOCUMENT IT IS ABOUT. One that only says
 * something happened, without opening the thing it happened to, is a nag: the
 * reader has to go and find the bill themselves, and the one they find may not
 * be the one meant.
 */
export function notificationLink(entityType, entityId) {
    const prefix = NOTIFICATION_LINK_PREFIX[entityType];
    return `${prefix}/${entityId}`;
}
//# sourceMappingURL=events.js.map